import React, { useState, useEffect, useRef } from 'react'
import {
  TextField, Stack, Typography, Box,
} from '@stvgroup/stv-ui-components'
import { WithSearch } from '@elastic/react-search-ui'
import PropTypes from 'prop-types'
import SimpleBar from 'simplebar-react'
import 'simplebar-react/dist/simplebar.min.css'
import SearchProviderWrapper from './SearchProvider'
import PopularCategories from './PopularCategories'
import SearchResults from './SearchResults'
import NoResults from './NoResults'
import ModalLayout from '../ModalLayout'

const Search = ({ toggleSearch }) => {
  const inputRef = useRef(null)
  const closeButtonRef = useRef(null)
  const containerRef = useRef(null)

  useEffect(() => {
    // Focus the input field when the component mounts.
    // The timeout ensures that the focus is set after the component has rendered.
    const timer = setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus()
      }
    }, 100)

    /**
     * Handles tab navigation within the search component to create a focus trap.
     * This ensures that users cannot tab outside of the search modal when it is open,
     * improving accessibility.
     */
    const handleKeyDown = (event) => {
      if (event.key === 'Tab' && containerRef.current) {
        const focusableElements = containerRef.current.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
        )
        const firstElement = focusableElements[0]
        const lastElement = focusableElements[focusableElements.length - 1]

        if (event.shiftKey) {
          // If shift + tab is pressed on the first focusable element,
          // focus is moved to the last focusable element.
          if (document.activeElement === firstElement) {
            lastElement.focus()
            event.preventDefault()
          }
        } else if (document.activeElement === lastElement) {
          // If tab is pressed on the last focusable element,
          // focus is moved to the first focusable element.
          firstElement.focus()
          event.preventDefault()
        }
      }
    }

    document.addEventListener('keydown', handleKeyDown)

    return () => {
      clearTimeout(timer)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [])

  const handleClose = (e) => {
    e.stopPropagation()
    toggleSearch()
  }

  return (
    <SearchProviderWrapper>
      <WithSearch
        mapContextToProps={({
          results, searchTerm, setSearchTerm, wasSearched, isLoading,
        }) => ({
          results,
          searchTerm,
          setSearchTerm,
          wasSearched,
          isLoading,
        })}
      >
        {({
          results, searchTerm, setSearchTerm, wasSearched, isLoading,
        }) => {
          const [inputValue, setInputValue] = useState(searchTerm)

          // This effect syncs the inputValue state with the searchTerm from the search context.
          // This is necessary because the searchTerm can be updated from other components.
          useEffect(() => {
            if (inputValue !== searchTerm) {
              setInputValue(searchTerm)
            }
          }, [searchTerm])

          // This effect debounces the search input.
          // It only calls setSearchTerm when the user has stopped typing for 500ms.
          useEffect(() => {
            const handler = setTimeout(() => {
              if (inputValue.length === 0 || inputValue.length > 1) {
                setSearchTerm(inputValue, { shouldClearFilters: true, refresh: true })
              }
            }, 500)

            return () => {
              clearTimeout(handler)
            }
          }, [inputValue, setSearchTerm])

          const hasResults = results && results.length > 0
          const hasSearchTerm = searchTerm.length > 0
          const loading = isLoading || (hasSearchTerm && !wasSearched)
          const handleInputChange = (value) => {
            setInputValue(value)
          }

          return (
            <ModalLayout ref={containerRef}>
              <ModalLayout.Header
                onClose={handleClose}
                closeButtonProps={{
                  ref: closeButtonRef,
                  ariaLabel: 'Close search',
                }}
                startSlot={(
                  <Typography variant="h2" sx={{ m: '0 auto', lineHeight: '1 !important' }}>
                    Search
                  </Typography>
                )}
                sx={{ mb: 2 }}
              />
              <ModalLayout.Content>
                <Stack spacing={0} sx={{ width: '100%' }}>
                  <TextField
                    inputRef={inputRef}
                    value={inputValue}
                    onChange={e => handleInputChange(e.target.value)}
                    placeholder="Search by show or movie name"
                    autoComplete="off"
                    sx={{
                      mb: 0,
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'background.paper',
                      },
                    }}
                  />
                  <Typography variant="h3" sx={{ mt: { xs: 1, sm: 2 }, mb: 1 }}>
                    {hasSearchTerm ? 'Search results' : 'Popular categories'}
                  </Typography>
                </Stack>
                <Box sx={{ flex: 1, minHeight: 0, width: '100%' }}>
                  <SimpleBar tabIndex={-1} style={{ height: '100%', overflowX: 'hidden' }}>
                    <Box sx={{ flex: 1, width: '100%' }}>
                      {loading && hasSearchTerm && <SearchResults loading usePoster />}
                      {!loading && hasResults && hasSearchTerm && (
                        <SearchResults results={results} usePoster />
                      )}
                      {!loading && !hasResults && hasSearchTerm && <NoResults />}
                      {!hasSearchTerm && <PopularCategories />}
                    </Box>
                  </SimpleBar>
                </Box>
              </ModalLayout.Content>
            </ModalLayout>
          )
        }}
      </WithSearch>
    </SearchProviderWrapper>
  )
}

Search.propTypes = {
  toggleSearch: PropTypes.func.isRequired,
}

export default Search
